/**
 * HTTP client for Candid coverage APIs via the shared api-client.
 *
 * @module candidService
 */
import { api } from "@risalabs_frontend_org/api-client";
import { API_ENDPOINTS } from "../../../enums/apiUrls";
import { CandidAuthorizationsResponse } from "../../../data-model/candidAuthorizationsResponse";
import {
  AllPayersRequest,
  AllPayersResponse,
  CandidAuthorizationsRequest,
  CandidDocumentUploadRequest,
  CandidNotesUploadRequest,
  CandidPayer,
} from "./candidApiTypes";
import {
  CandidWritebackHistoryRequest,
  WritebackHistoryResult,
} from "./candidWritebackHistoryTypes";

/**
 * Service for Candid coverage APIs used by the auth-letter / Candid UI.
 */
export class CandidService {
  /**
   * Looks up payers in Candid's directory for a facility.
   *
   * Bearer token is injected by {@link api.post}.
   *
   * @param payload - Org id and optional payer-name search term
   * @param signal - Optional abort signal for in-flight cancellation
   * @returns Payer rows from the backend list envelope
   * @throws Error when auth is missing or the HTTP request fails
   */
  static async getAllPayers(
    payload: AllPayersRequest,
    signal?: AbortSignal,
  ): Promise<CandidPayer[]> {
    const response = await api.post<AllPayersRequest, AllPayersResponse>(
      API_ENDPOINTS.CANDID_PAYERS,
      payload,
      signal,
    );
    return response.payers ?? response.data ?? [];
  }

  /**
   * Posts authorization records to Candid for an order.
   *
   * Bearer token is injected by {@link api.post}.
   *
   * @param payload - Order context plus authorization entries to write back
   * @param signal - Optional abort signal for in-flight cancellation
   * @returns Written authorizations envelope (`order_id`, `status`, count, pairs)
   * @throws Error when auth is missing or the HTTP request fails
   */
  static async postAuthorizations(
    payload: CandidAuthorizationsRequest,
    signal?: AbortSignal,
  ): Promise<CandidAuthorizationsResponse> {
    return api.post<CandidAuthorizationsRequest, CandidAuthorizationsResponse>(
      API_ENDPOINTS.CANDID_AUTHORIZATIONS,
      payload,
      signal,
    );
  }

  /**
   * Posts an uploaded document's GCS path to Candid for an order.
   *
   * Bearer token is injected by {@link api.post}.
   *
   * @param payload - Order context plus document path and display name
   * @param signal - Optional abort signal for in-flight cancellation
   * @returns The backend response envelope
   * @throws Error when auth is missing or the HTTP request fails
   */
  static async postDocument(
    payload: CandidDocumentUploadRequest,
    signal?: AbortSignal,
  ): Promise<unknown> {
    return api.post<CandidDocumentUploadRequest, unknown>(
      API_ENDPOINTS.CANDID_DOCUMENT_UPLOAD,
      payload,
      signal,
    );
  }

  /**
   * Posts a note to Candid for an order.
   *
   * Bearer token is injected by {@link api.post}.
   *
   * @param payload - Order context plus the note text to write back
   * @param signal - Optional abort signal for in-flight cancellation
   * @returns The backend response envelope
   * @throws Error when auth is missing or the HTTP request fails
   */
  static async postNote(
    payload: CandidNotesUploadRequest,
    signal?: AbortSignal,
  ): Promise<unknown> {
    return api.post<CandidNotesUploadRequest, unknown>(
      API_ENDPOINTS.CANDID_NOTES_UPLOAD,
      payload,
      signal,
    );
  }

  /**
   * Fetches Candid write-back attempt history for an order, newest first.
   *
   * Bearer token is injected by {@link api.post}.
   *
   * @param payload - Order id and org id that own the attempts
   * @param signal - Optional abort signal for in-flight cancellation
   * @returns History envelope with attempts and count
   * @throws Error when auth is missing or the HTTP request fails
   */
  static async getWritebackHistory(
    payload: CandidWritebackHistoryRequest,
    signal?: AbortSignal,
  ): Promise<WritebackHistoryResult> {
    return api.post<CandidWritebackHistoryRequest, WritebackHistoryResult>(
      API_ENDPOINTS.CANDID_WRITEBACK_HISTORY,
      payload,
      signal,
    );
  }
}
